/**
 * Edits that act on several entities at once, as pure request builders: one
 * command for one entity, or a labeled group that runs as one transaction
 * (one undo step). The keyboard, the Outliner, the viewport menu and the
 * Inspector share them, so Hide means the same everywhere.
 *
 * Which entities an action takes follows Unreal: acting on a selected entity
 * acts on the whole selection; acting on an unselected one acts on it alone.
 */
import type { CommandOrigin, CommandRequest } from "../../commands/types";
import type { EntityId, EntityRecord } from "../../world/entity";
import { isLevelRoot, subtreeOf, topmostOf } from "../../world/hierarchy";
import type { SelectionState } from "./Selection";

/** Several requests that run as one transaction under `label`. */
export interface RequestGroup {
  readonly label: string;
  readonly requests: readonly CommandRequest[];
}

/** One command, or a group of them as one undo step. */
export type Edit = CommandRequest | RequestGroup;

export function isGroup(edit: Edit): edit is RequestGroup {
  return "requests" in edit;
}

export interface VisibilityTarget {
  readonly id: string;
  readonly visible: boolean;
}

/** Shows or hides the targets, skipping any already so; null when nothing changes. */
export function setVisibleEdit(
  targets: readonly VisibilityTarget[],
  visible: boolean,
  origin: CommandOrigin,
): Edit | null {
  const requests = targets
    .filter((target) => target.visible !== visible)
    .map((target) => ({
      type: "entity.setVisible",
      payload: { entity: target.id, visible },
      origin,
    }));

  const [only] = requests;
  if (!only) return null;
  if (requests.length === 1) return only;
  return { label: `${visible ? "Show" : "Hide"} ${requests.length} entities`, requests };
}

/**
 * Deletes `ids` (each topmost one, never the level root) as one step. The
 * selection first drops everything that goes, so undo restores it along with
 * the entities. Null when nothing can be deleted.
 */
export function deleteEdit(
  entities: ReadonlyMap<EntityId, EntityRecord>,
  selection: SelectionState,
  ids: readonly EntityId[],
  origin: CommandOrigin,
): RequestGroup | null {
  // The level root stays, but its selected children still go.
  const deletable = ids.filter((id) => {
    const record = entities.get(id);
    return record !== undefined && !isLevelRoot(record);
  });
  const doomed = topmostOf(entities, deletable);
  const [only] = doomed;
  if (only === undefined) return null;

  const gone = new Set(doomed.flatMap((id) => subtreeOf(entities, id)));
  const kept = selection.ids.filter((id) => !gone.has(id));
  const active =
    selection.active !== null && !gone.has(selection.active) ? selection.active : kept.at(-1);

  return {
    label:
      doomed.length === 1
        ? `Delete ${entities.get(only)?.name ?? only}`
        : `Delete ${doomed.length} entities`,
    requests: [
      {
        type: "selection.set",
        payload: { ids: kept, ...(active !== undefined && { active }) },
        origin,
      },
      ...doomed.map((entity) => ({ type: "entity.delete", payload: { entity }, origin })),
    ],
  };
}

/** The entities an action on `id` takes: the whole selection when `id` is in it. */
export function actionTargets(selection: SelectionState, id: EntityId): EntityId[] {
  return selection.ids.includes(id) ? [...selection.ids] : [id];
}

/** The visibility of each entity that exists, for `setVisibleEdit`. */
export function visibilityOf(
  world: { get(id: EntityId): EntityRecord | undefined },
  ids: readonly EntityId[],
): VisibilityTarget[] {
  return ids.flatMap((id) => {
    const entity = world.get(id);
    return entity ? [{ id, visible: entity.visible }] : [];
  });
}
